import React, { useMemo } from "react";
import OrganizedTourProfileSection from "./OrganizedTourProfileSection";
import { FaCircleInfo } from "react-icons/fa6";
import {
  resolveOrgTourInfoDisplay,
  ORG_TOUR_ITINERARY_SOFT_NOTICE_HE,
  getOrgTourInfoDangerousInnerHtml,
  getOrgTourInfoPlainTextForDisplay,
} from "../../../../utils/orgTourInfoDisplay";
import { useOrgTourInfoDevDisplayTrace } from "../../../../utils/useOrgTourInfoDevDisplayTrace";
import {
  OrganizedTourItineraryNarrativeAccordion,
  resolveOrganizedTourItineraryPlainBody,
} from "../../../../modules/organizedTours/components/OrganizedTourItineraryNarrativeAccordion";
import { sanitizeOrganizedTourResourceDescription } from "../../../../modules/organizedTours/utils/resolveOrganizedTourSearchCardPackageIls";

/**
 * LDS getOrgTourInfo — only server-sanitized HTML or plain text (no raw supplier source).
 */
export default function OrganizedTourInfoSection({
  loading,
  error,
  missingFields = [],
  contentText,
  contentHtmlSanitized,
  resourceUrl,
  resourceDescription,
  resourceFetchOk,
}) {
  const display = useMemo(
    () =>
      resolveOrgTourInfoDisplay({
        contentHtmlSanitized,
        contentText,
      }),
    [contentHtmlSanitized, contentText]
  );

  useOrgTourInfoDevDisplayTrace(display, contentHtmlSanitized, contentText);

  const safeResourceDescription = useMemo(
    () => sanitizeOrganizedTourResourceDescription(resourceDescription),
    [resourceDescription]
  );

  const renderBody = () => {
    if (loading) {
      return (
        <p className="otp-tour-info__loading" role="status">
          טוענים מידע מורחב על הטיול...
        </p>
      );
    }

    if (missingFields?.length) {
      const labels = {
        tourName: "שם הטיול",
        supplierId: "מזהה ספק",
        dealId: "מזהה הצעה",
      };
      return (
        <p className="otp-tour-info__error">
          לא ניתן לטעון מידע מהספק — חסרים נתונים:{" "}
          {missingFields.map((f) => labels[f] || f).join(", ")}. נסו לפתוח את
          הפרופיל מתוצאות החיפוש.
        </p>
      );
    }

    if (error && display.mode === "none") {
      return (
        <p className="otp-tour-info__error" role="note">
          {ORG_TOUR_ITINERARY_SOFT_NOTICE_HE}
          {resourceUrl ? (
            <>
              {" "}
              <a
                href={resourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="otp-tour-info__link"
              >
                פתיחת מידע אצל הספק
              </a>
            </>
          ) : null}
        </p>
      );
    }

    if (display.mode === "html") {
      const safeInner = getOrgTourInfoDangerousInnerHtml(display);
      if (!safeInner) {
        return (
          <div className="otp-tour-info__text">
            {safeResourceDescription ? (
              <p className="otp-tour-info__desc">{safeResourceDescription}</p>
            ) : null}
            <p className="otp-tour-info__empty">{ORG_TOUR_ITINERARY_SOFT_NOTICE_HE}</p>
          </div>
        );
      }
      const plain = resolveOrganizedTourItineraryPlainBody(null, safeInner);
      return (
        <div className="otp-tour-info__text">
          {safeResourceDescription ? (
            <p className="otp-tour-info__desc">{safeResourceDescription}</p>
          ) : null}
          {plain ? (
            <OrganizedTourItineraryNarrativeAccordion
              narrativeHtml={safeInner}
              narrativeText={null}
              instanceId="otp-legacy-profile-html"
            />
          ) : (
            <p className="otp-tour-info__empty">{ORG_TOUR_ITINERARY_SOFT_NOTICE_HE}</p>
          )}
        </div>
      );
    }

    if (display.mode === "text") {
      const safePlain = getOrgTourInfoPlainTextForDisplay(display);
      const plain = safePlain ? resolveOrganizedTourItineraryPlainBody(safePlain, null) : "";
      return (
        <div className="otp-tour-info__text">
          {safeResourceDescription ? (
            <p className="otp-tour-info__desc">{safeResourceDescription}</p>
          ) : null}
          {plain ? (
            <OrganizedTourItineraryNarrativeAccordion
              narrativeHtml={null}
              narrativeText={safePlain}
              instanceId="otp-legacy-profile-text"
            />
          ) : (
            <p className="otp-tour-info__empty">{ORG_TOUR_ITINERARY_SOFT_NOTICE_HE}</p>
          )}
        </div>
      );
    }

    if (resourceUrl && !resourceFetchOk) {
      return (
        <>
          <p className="otp-tour-info__warn">
            המידע הטקסטואלי לא נטען אצלנו. ניתן לצפות בדף הספק:
          </p>
          <a
            href={resourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="otp-tour-info__link"
          >
            פתיחת מידע על הטיול
          </a>
        </>
      );
    }

    return <p className="otp-tour-info__empty">{ORG_TOUR_ITINERARY_SOFT_NOTICE_HE}</p>;
  };

  return (
    <OrganizedTourProfileSection
      id="otp-section-tour-info"
      title="מידע על הטיול"
      icon={FaCircleInfo}
    >
      <div className="otp-tour-info">{renderBody()}</div>
    </OrganizedTourProfileSection>
  );
}
